import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Logger from '../../../common/logger';
import { TenantPayments } from '../../../api/account/accountApiTypes';
import { assetApiSlice } from '../../../api/asset/assetApiSlice';
import { PricingDiscount } from '../../../api/asset/assetApiTypes';
import {
    AdminProduct,
    groupAdminApiSlice,
    useUpdateProductMutation,
} from '../../../api/group/groupAdminApiSlice';
import { useUpdatePriceScalesMutation } from '../../../api/priceCategory/priceCategoryApiSlice';
import { useTenantProductCategoriesQuery } from '../../../hooks/useTenantProductCategoriesQuery';
import CustomButton from '../../../components/CustomButton';
import { errorMessageFrom } from '../../../utils/errorMessage';
import { priceScaleAmountsValid, validatePricingDiscounts } from '../../../utils/pricingValidation';
import PricingSection from '../../assets/components/PricingSection';
import { toPriceScalePayload } from '../../assets/priceScalePayload';
import { makeInitialRoomPriceScales, toEditablePriceScales } from '../../assets/roomPriceScales';
import CustomModal from '../../common/CustomModal';
import { isPriceLockedForProduct } from '../serviceConfigurationRules';
import { spaceOf } from '../../../utils/useCases';

interface Props {
    /** A venue or apartment product. */
    product: AdminProduct;
    payments: TenantPayments | null;
    onClose: () => void;
}

/**
 * The price sheet of a ROOM product, edited on the product (#2923; PO
 * decision 2026-09-28, option B).
 *
 * A room type — "1-Zimmer/Küche/Bad" — prices every object assigned to it
 * from its default Offer's list (`resolveRateSource`), and the object page
 * locks its own editor as soon as more than one object shares the product
 * (#2876). Without this dialog a shared room product's price could be set
 * nowhere, and one created in Services — born with no rows on a paid tenant —
 * would leave every apartment assigned to it priced with nothing (ADR-0018).
 *
 * It is the object page's room editor, not the tariff editor: room sheets are
 * a nightly row (apartment) or weekday + weekend rows (venue), which the
 * tariff dialog does not speak.
 *
 * The long-stay discounts are edited HERE too (option b, PO 2026-09-28): the
 * column stays `asset.pricing_discounts`, but the value is the PRODUCT's —
 * identical on every object, written to all of them by the product PATCH.
 * When the objects currently DIVERGE (pre-option-b data), the dialog says so
 * and makes the operator choose which set becomes the product's — never
 * silently the first asset's (a failure must not look like an absence).
 *
 * Mounted per product, so the rows seed from the list once (edit-form rule).
 */
const RoomProductPricesDialog = ({ product, payments, onClose }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const useCase = (product.useCase ?? product.compartmentTypes[0] ?? '').toLowerCase();
    const priceCategoryId = product.priceCategory?.id ?? null;
    // A free offer's rows are generated zeros and not the tenant's to price
    // (ADR-0018 rule 4) — free by its use case, or since ADR-0026 §W by the
    // product category it is filed under.
    const { data: categories } = useTenantProductCategoriesQuery();
    const locked = isPriceLockedForProduct(
        useCase, product.productCategoryId, categories ?? [], payments,
    );

    // An empty sheet starts from the rows a room needs, with EMPTY prices, so
    // the operator types every one — never a silent 0.
    // `?? []`, not trusted: the admin UI can deploy ahead of the backend that
    // adds `priceScales`, and an undefined list must open an empty sheet
    // rather than crash the page.
    const storedScales = product.priceScales ?? [];
    const [scales, setScales] = useState(() => (storedScales.length > 0
        ? toEditablePriceScales(storedScales)
        : makeInitialRoomPriceScales(useCase)));
    // Option b: discounts belong to the stay pricing rule (apartments; the
    // venue editor never had them) and are product-owned. On divergence they
    // start UNRESOLVED — the operator picks a set below before the editor appears.
    const isApartment = spaceOf(useCase)?.flow === 'stay';
    // Option b stores tiers ON the objects — with none assigned yet there is
    // no home for them, and offering the editor would accept an edit that
    // vanishes on reopen (a failure looking like an absence).
    const hasObjects = (product.objectCount ?? 0) > 0;
    const diverged = isApartment && (product.discountsDiverge ?? false);
    const [discountsResolved, setDiscountsResolved] = useState(!diverged);
    const [discounts, setDiscounts] = useState<PricingDiscount[]>(
        () => (Array.isArray(product.pricingDiscounts) ? product.pricingDiscounts : []),
    );
    const [formError, setFormError] = useState<string | null>(null);
    const [updatePriceScales, { isLoading }] = useUpdatePriceScalesMutation();
    const [updateProduct, { isLoading: savingDiscounts }] = useUpdateProductMutation();
    const saving = isLoading || savingDiscounts;

    const editable = !!priceCategoryId && !locked;

    const handleSave = async () => {
        if (!priceCategoryId || locked || saving) return;
        if (!priceScaleAmountsValid(scales)) {
            setFormError(t('services.products.prices.invalid'));
            return;
        }
        if (isApartment && hasObjects && discountsResolved) {
            const discountError = validatePricingDiscounts(discounts);
            if (discountError) {
                setFormError(t(discountError.key));
                return;
            }
        }
        setFormError(null);
        try {
            await updatePriceScales({
                priceCategoryId,
                pricescales: toPriceScalePayload(scales),
            }).unwrap();
            // The discounts go through the product PATCH, which writes the
            // one value to every live asset of every cell. Skipped while a
            // divergence is unresolved — unchanged is the only safe write.
            if (isApartment && hasObjects && discountsResolved) {
                await updateProduct({
                    productId: product.id,
                    pricingDiscounts: JSON.stringify(discounts),
                }).unwrap();
            }
            // The price slice declares no tags of its own: refresh what reads
            // this sheet — the product list, the Services price view and the
            // object pages of every room assigned to the product.
            dispatch(groupAdminApiSlice.util.invalidateTags(['Products', 'PricingStructure']));
            dispatch(assetApiSlice.util.invalidateTags(['assetDetails']));
            onClose();
        } catch (err) {
            Logger('RoomProductPricesDialog').error(`Price save failed: ${JSON.stringify(err)}`);
            setFormError(errorMessageFrom(err, t('services.products.prices.save_error'), t));
        }
    };

    return (
        <CustomModal
            open={true}
            title={t('services.products.prices.title', { name: product.name ?? '' })}
            description={t('services.products.prices.description')}
            onClose={onClose}
            closeButton={
                <CustomButton title={t('common.cancel')} isFilled={false} onClick={onClose} />
            }
            saveButton={editable ? (
                <CustomButton
                    title={t('common.save')}
                    loading={saving}
                    onClick={handleSave}
                />
            ) : null}
        >
            <div className={'flex flex-col gap-4'}>
                {!priceCategoryId && (
                    <p className={'text-sm text-foreground/60'}>
                        {t('services.products.prices.no_price_list')}
                    </p>
                )}
                {priceCategoryId && (
                    <PricingSection
                        priceCategoryName={product.priceCategory?.name}
                        priceScales={storedScales}
                        pricingDiscounts={null}
                        useCase={useCase}
                        mode={editable ? 'edit' : 'view'}
                        editPriceScales={editable ? scales : undefined}
                        onPriceScalesChange={setScales}
                        showDiscounts={isApartment && hasObjects && discountsResolved}
                        editDiscounts={editable && discountsResolved ? discounts : undefined}
                        onDiscountsChange={setDiscounts}
                        priceCategoryId={priceCategoryId}
                    />
                )}
                {isApartment && !hasObjects && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('services.products.prices.discounts_need_object')}
                    </p>
                )}
                {diverged && !discountsResolved && (
                    <div className={'flex flex-col gap-2 rounded-lg border border-foreground/20 p-3'}>
                        <p className={'text-sm font-medium text-foreground'}>
                            {t('services.products.prices.discounts_diverge')}
                        </p>
                        <p className={'text-xs text-foreground/60'}>
                            {t('services.products.prices.discounts_diverge_hint')}
                        </p>
                        {(product.discountSets ?? []).map((set, i) => (
                            <button
                                key={i}
                                type={'button'}
                                className={'text-left text-sm rounded-lg border border-foreground/10 px-3 py-2 hover:border-primary'}
                                onClick={() => {
                                    setDiscounts(set.discounts);
                                    setDiscountsResolved(true);
                                }}
                            >
                                <span className={'font-medium'}>
                                    {set.discounts.length === 0
                                        ? t('services.products.prices.discount_set_none')
                                        : set.discounts.map((d) => (
                                            `${t('asset_detail.fields.from')} ${d.nights[0]}: -${d.discount}%`
                                        )).join(' · ')}
                                </span>
                                <span className={'block text-xs text-foreground/60'}>
                                    {t('services.products.prices.discount_set_assets', { count: set.assetCount })}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
                {locked && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('services.pricing.free_locked')}
                    </p>
                )}
                {formError && <p className={'text-sm text-red-500'}>{formError}</p>}
            </div>
        </CustomModal>
    );
};

export default RoomProductPricesDialog;
